import React from 'react';
import { connect } from 'react-redux';
import { KFLButton, KFLCheckbox } from '@kamstrup/kamstrup-frontend-library';
import { ReduxState } from '../../interfaces';
import { MessageType } from '../../core/localization';
import Translate from '../shared/localization/localize';
import * as ReadRights from '../../modules/dialogs/readRights';
import * as Devices from '../../modules/devices';
import * as Orders from '../../modules/orders';
import * as DTypes from '../../modules/devicetypes';
import * as DT from '../../modules/datatable';
import { Groups, SelectMode } from '../../models';
import Spinner from '../shared/spinner';
import FlaggedMsg, { FlagTypes } from '../shared/flaggedMsg';
import { setConfirmed } from '../../modules/dialogs/readRights';

interface StateProps extends ReadRights.State {
  devicesData: Devices.Data;
  ordersData: DT.IData<Orders.Order>;
  dtypesData: DT.IData<DTypes.DeviceType>;
}

type Props = StateProps;

const DeviceReadRightDetails: React.FC<Props> = (props: Props) => {
  const { keys, devicesData, ordersData, dtypesData, isConfirmed } = props;
  const hasKeys = keys.length > 0;
  let warnMsg: MessageType = '';
  let devicesInvolved = keys.length;
  const warnDeviceLimit = 1000;

  switch (props.groupedBy) {
    case Groups.ORDERS:
      devicesInvolved = Orders.getNumDevicesFromOrders(keys, ordersData);
      if (!hasKeys) warnMsg = ReadRights.msgNoOrders;
      break;
    case Groups.DEVICETYPES:
      devicesInvolved = DTypes.getNumDevicesFromDTypes(keys, dtypesData);
      if (!hasKeys) warnMsg = ReadRights.msgNoProducts;
      break;
    case Groups.DEVICES:
      if (props.mode === SelectMode.All) devicesInvolved = devicesData.total;
      break;
    default:
      break;
  }

  const hasDevices = devicesInvolved > 0;
  const mainMsg = ReadRights.msgNumDevices(devicesInvolved);
  const confirmDevicesMsg = ReadRights.msgConfirmDevices();
  const hasWarning = !(warnMsg === '');
  const warning = <FlaggedMsg type={FlagTypes.Warning} label={warnMsg} />;
  const showConfirmCheckbox = (devicesInvolved >= warnDeviceLimit);
  const toggleConfirmedDevices = () => setConfirmed(!isConfirmed);
  const btnsDisabled =
    props.assignCall.isFetching || props.unassignCall.isFetching || (showConfirmCheckbox && !isConfirmed);

  const btnAssign = (
    <KFLButton
      id="readrights-change"
      disabled={btnsDisabled}
      onClick={() => ReadRights.openAssignDialog()}
    >
      <Translate message={ReadRights.msgAssignBtn} />
    </KFLButton>
  );

  const isRemoving = props.unassignCall.isFetching;
  const removing = <Spinner label={ReadRights.msgUpdating} iconSize={20} />;
  const btnRemove = (
    <KFLButton
      id="readrights-remove"
      variant="secondary"
      onClick={() => ReadRights.removeReadRights(keys)}
      disabled={btnsDisabled}
    >
      <Translate message={ReadRights.msgUnassignBtn} />
    </KFLButton>
  );

  return (
    <div className='readright-detail'>
      <div className='device-involved'>
        <Translate message={mainMsg} />
      </div>
      <div>{hasWarning && warning}</div>
      {hasDevices && (
        <div>
          {isRemoving ? (
            removing
          ) : (
              <>
                {showConfirmCheckbox && (<div className='checkbox-container'>
                  <KFLCheckbox
                    id="readrights-cb-confirm"
                    checked={isConfirmed}
                    onChange={toggleConfirmedDevices}
                  />
                  <span className='label' onClick={toggleConfirmedDevices}>
                    <Translate message={confirmDevicesMsg} />
                  </span>
                </div>)}
                <div className="edit_options">
                  <div>{btnRemove}</div>
                  <div className="separator" />
                  <div>{btnAssign}</div>
                </div>
              </>
            )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  ...state.dialogState.readrightState,
  devicesData: state.devicesState.data,
  ordersData: state.ordersState.data,
  dtypesData: state.deviceTypesState.data,
});

export default connect(mapStateToProps)(DeviceReadRightDetails);
